import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { User, Loader2, Plus, Edit, Trash2 } from "lucide-react";
import type { Account, AccountPeople, Contact } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ContactSelect } from "@/components/form-fields/contact-select";
import { ContactFormModal } from "@/components/contact-form-modal";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { buildNavigationUrl } from "@/lib/breadcrumbs";

interface ProprietorWithContact extends AccountPeople {
  contact?: Contact;
}

interface ProprietorPanelProps {
  account: Account;
}

export function ProprietorPanel({ account }: ProprietorPanelProps) {
  // All hooks must be called before any early returns (React rules of hooks)
  const [, navigate] = useLocation();
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [showCreateContactDialog, setShowCreateContactDialog] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { toast } = useToast();

  // Only show for sole_trader entity type
  const isSoleTrader = account.entityType === "sole_trader";

  const { data: proprietors = [], isLoading } = useQuery<ProprietorWithContact[]>({
    queryKey: ["/api/crm/accounts", account.id, "proprietor"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/accounts/${account.id}/people?role=proprietor`);
      return response.json();
    },
    enabled: isSoleTrader, // Skip query for non-sole-traders
  });

  const { data: _contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { clientGroupId: account.clientGroupId }],
    enabled: isLinkDialogOpen && isSoleTrader,
  });

  // Business rule: Only one proprietor per sole trader (1:1 relationship)
  const proprietor = proprietors.length > 0 ? proprietors[0] : null;

  const linkMutation = useMutation({
    mutationFn: async (contactId: string) => {
      // If proprietor already exists, replace it
      if (proprietor) {
        await apiRequest("DELETE", `/api/crm/accounts/${account.id}/people/${proprietor.id}`);
      }
      await apiRequest("POST", `/api/crm/accounts/${account.id}/people`, {
        contactId,
        role: "proprietor",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "proprietor"] });
      toast({ title: "Proprietor linked", description: "The proprietor has been linked successfully." });
      handleCloseDialog();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to link proprietor", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!proprietor) return;
      await apiRequest("DELETE", `/api/crm/accounts/${account.id}/people/${proprietor.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/accounts", account.id, "proprietor"] });
      toast({ title: "Proprietor removed", description: "The proprietor link has been removed." });
      setIsDeleteDialogOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to remove proprietor", variant: "destructive" });
    },
  });

  // Early return AFTER hooks to comply with React rules of hooks
  if (!isSoleTrader) {
    return null;
  }

  const handleCloseDialog = () => {
    setIsLinkDialogOpen(false);
    setSelectedContactId(null);
  };

  const handleLink = () => {
    if (!selectedContactId) {
      toast({ title: "Validation Error", description: "Please select a contact", variant: "destructive" });
      return;
    }

    linkMutation.mutate(selectedContactId);
  };

  const handleContactClick = () => {
    if (!proprietor?.contactId || !proprietor.contact) return;
    const url = buildNavigationUrl(`/contacts/${proprietor.contactId}`, {
      from: "account",
      fromId: account.id,
      fromLabel: account.legalName || "Account",
    });
    navigate(url);
  };

  const handleContactCreated = (newContact: Contact) => {
    setSelectedContactId(newContact.id);
    setShowCreateContactDialog(false);
    toast({ title: "Contact created", description: "You can now select the newly created contact." });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Proprietor</h3>
        </div>
        {!proprietor && (
          <Button
            size="sm"
            onClick={() => setIsLinkDialogOpen(true)}
            data-testid="button-link-proprietor"
          >
            Link Proprietor
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : !proprietor ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md bg-white">
          <User className="h-10 w-10 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No proprietor linked</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click "Link Proprietor" to link a contact as the sole trader proprietor
          </p>
        </div>
      ) : (
        <Card className="border-l-4 border-l-blue-500 bg-white">
          <CardContent className="p-3">
            <div className="flex justify-between items-start">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <button
                    onClick={handleContactClick}
                    className="font-medium text-sm text-primary hover:underline"
                    data-testid="link-proprietor-contact"
                  >
                    {proprietor.contact
                      ? `${proprietor.contact.firstName || ""} ${proprietor.contact.lastName || ""}`.trim() || "Unknown"
                      : "Unknown Contact"}
                  </button>
                </div>

                <p className="text-xs text-muted-foreground">
                  Proprietor - contact
                </p>

                {proprietor.appointmentDate && (
                  <p className="text-xs text-muted-foreground">
                    Appointed: {format(new Date(proprietor.appointmentDate), "dd MMM yyyy")}
                  </p>
                )}
              </div>

              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsLinkDialogOpen(true)}
                  data-testid="button-edit-proprietor"
                  title="Change proprietor"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  data-testid="button-delete-proprietor"
                  title="Remove proprietor"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Link Proprietor Dialog */}
      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{proprietor ? "Change" : "Link"} Proprietor</DialogTitle>
            <DialogDescription>
              {proprietor
                ? "Select a different contact to replace the current proprietor"
                : "Link a contact as the proprietor for this sole trader"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Contact</Label>
              <ContactSelect
                value={selectedContactId || undefined}
                onValueChange={(value) => setSelectedContactId(value)}
                clientGroupId={account.clientGroupId || undefined}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateContactDialog(true)}
                className="mt-2 w-full"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Contact
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button onClick={handleLink} disabled={linkMutation.isPending}>
              {linkMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {proprietor ? "Change" : "Link"} Proprietor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Creation Modal */}
      <ContactFormModal
        open={showCreateContactDialog}
        onOpenChange={setShowCreateContactDialog}
        onContactCreated={handleContactCreated}
        defaultClientGroupId={account.clientGroupId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Proprietor</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove the proprietor link from this sole trader?
              This will unlink the contact but will not delete the contact record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete-proprietor"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
